import { useRef, useState, type FormEvent } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import environment, { apiKeys } from "@/modules/environment";
import { signup } from "@/modules/cloud/user";
import { AUTH_ERROR } from "@/const/auth";
import { I18n } from "@/i18n";
import Trans from "@/i18n/Trans";
import { useRouter } from "@/router";
import useI18n from "@/hooks/useI18n";
import useControl from "@/hooks/useControl";
import { css, cx } from "@styled-system/css";
import { button } from "@styled-system/recipes";
import GenericFailure from "@/components/react/GenericFailure";
import Input from "@/components/react/Input";
import Link from "@/components/react/Link";
import PageContainer from "@/components/react/PageContainer";
import { verifyGoogleToken } from "@/utils/verifyGoogleToken";
import { getRouteBasedOnQuery } from "@/utils/paths";
import getPrivacyPolicyLinkByLocale from "@/utils/getPrivacyPolicyLinkByLocale";

export default function Signup() {
  const { t } = useI18n();
  const router = useRouter();
  const authErrorDialog = useControl();
  const captchaErrorDialog = useControl();

  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [family, setFamily] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCaptchaComplete, setIsCaptchaComplete] = useState(false);

  const recaptchaRef = useRef<ReCAPTCHA>(null);

  async function handleSignup(e: FormEvent) {
    e.preventDefault();

    setIsLoading(true);

    if (!environment.isDev && recaptchaRef.current) {
      const token = await recaptchaRef.current.executeAsync();
      const passed = await verifyGoogleToken({ token: token || "", isCaptchaComplete });
      if (!passed) {
        captchaErrorDialog.open();
        setIsLoading(false);
        return;
      }
    }

    const code = router.query?.code;
    const result = await signup({
      email: email!,
      password: password!,
      familyName: family!,
      promoCode: code
    });

    if ("err" in result) {
      if (result.err === AUTH_ERROR.ALREADY_EXISTS) authErrorDialog.open();
      setIsLoading(false);
      return;
    }

    let promoResult = {};
    if (result?.promoCodeError) promoResult = { prError: 1 };
    if (code && !result?.promoCodeError) promoResult = { prResult: 1 };

    const { pathname, query } = await getRouteBasedOnQuery();
    if (pathname === "/webapp") window.open(pathname, "_self");
    else router.push(pathname, { query: { ...query, ...promoResult } });
  }

  // prevents server render error
  const ReCAPTCHAComponent = ((ReCAPTCHA as any).default || ReCAPTCHA) as typeof ReCAPTCHA;
  const isInAFlow = ["purchase", "giftcard"].includes(router.query.from || "");
  return (
    <>
      <PageContainer
        onSubmit={handleSignup}
        image="account.svg"
        title={t(isInAFlow ? "account.signup.createToContinue" : "account.signup.heading")}
        subtitle={
          <Trans i18nKey="account.signup.alreadyRegistered">
            <Link aria-label="login" href="/login" query={router.query} className={css({ color: "primary" })}>
              Log in
            </Link>
          </Trans>
        }
      >
        <Input
          dispatchEventOnLoad
          attributes={{
            autoComplete: "username",
            required: true,
            autoFocus: true,
            type: "email"
          }}
          className={css({
            maxWidth: "22rem",
            mb: "1.5rem"
          })}
          label={t("account.placeholder.email")}
          value={email}
          onInput={(e) => setEmail(e.target.value)}
        />
        <Input
          dispatchEventOnLoad
          attributes={{
            autoComplete: "new-password",
            minLength: 6,
            required: true,
            type: "password",
            "aria-autocomplete": "list"
          }}
          className={css({
            maxWidth: "22rem",
            mb: "1.5rem"
          })}
          label={t("account.placeholder.password")}
          value={password}
          onInput={(e) => setPassword(e.target.value)}
        />
        <Input
          dispatchEventOnLoad
          attributes={{
            required: true,
            type: "text"
          }}
          className={css({
            maxWidth: "22rem",
            mb: "1.5rem"
          })}
          label={t("account.placeholder.familyname")}
          value={family}
          onInput={(e) => setFamily(e.target.value)}
        />
        {/* <NoSSR> */}
        <ReCAPTCHAComponent
          ref={recaptchaRef}
          sitekey={apiKeys.googleSiteKey}
          onChange={() => setIsCaptchaComplete(true)}
          size="invisible"
        />
        {/* </NoSSR> */}
        <button
          className={cx(
            button({ variant: "filled" }),
            css({
              mt: "2rem",
              mb: "5.375rem",
              maxWidth: "100%",
              width: "22rem"
            })
          )}
          disabled={isLoading}
          type="submit"
        >
          {t("account.button.createAccount")}
        </button>
        <p className={css({ textAlign: "center", mb: "1.5rem" })}>
          <Trans i18nKey="account.signup.tosAndPp">
            <Link href="/terms" className={css({ color: "primary" })}>
              Terms of Serive
            </Link>
            <Link className={css({ color: "primary" })} href={getPrivacyPolicyLinkByLocale(I18n.locale)} locale={false}>
              Privacy Policy
            </Link>
          </Trans>
        </p>
      </PageContainer>
      <GenericFailure
        isOpen={authErrorDialog.isOpen}
        onClose={authErrorDialog.close}
        title={t("account.userExists.heading")}
        subtitle={t("account.userExists.subtitle")}
      />
      <GenericFailure
        isOpen={captchaErrorDialog.isOpen}
        onClose={captchaErrorDialog.close}
        title={t("account.signup.captcha.heading")}
        subtitle={t("account.signup.captcha.subtitle")}
      />
    </>
  );
}
